import { useCallback, useEffect, useState } from 'react';

import { useVisualization } from '@/context/visualization-context';
import type { Color3DPresetId } from '@/visualization/services/color-3d-presets';
import { Color3DService } from '@/visualization/services/color-3d-service';
import type { WindowLevel } from '@/visualization/services/window-level-service';

export interface Color3DSnapshot {
    /** Effective preset id (the C-arm override while a draft is active). */
    presetId: Color3DPresetId;
    /** Live transfer-function window/level of the effective preset. */
    windowLevel: WindowLevel;
    /** The preset's authored window/level — what "reset to default" restores. */
    defaultWindowLevel: WindowLevel;
    /** Whether the effective preset carries a user adjustment. */
    isAdjusted: boolean;
}

/**
 * Thin bridge to the global Color3DService. Subscribes to both the preset and
 * the window/level events and returns the live 3D color state.
 *
 * The service is global — one 3D color state applies to every volume viewport
 * that isn't pinned to a fixed profile — so every consumer of this hook observes
 * the same values, updating live during drags and slider moves.
 */
export function useColor3D(): Color3DSnapshot {
    const { getServiceManager } = useVisualization();

    const buildSnapshot = useCallback((service: Color3DService): Color3DSnapshot => {
        return {
            presetId: service.getActivePresetId(),
            windowLevel: service.getWindowLevel(),
            defaultWindowLevel: service.getDefaultWindowLevel(),
            isAdjusted: service.isAdjusted(),
        };
    }, []);

    const [snapshot, setSnapshot] = useState<Color3DSnapshot>(() =>
        buildSnapshot(getServiceManager().getService<Color3DService>(Color3DService.SERVICE_NAME))
    );

    useEffect(() => {
        const service = getServiceManager().getService<Color3DService>(Color3DService.SERVICE_NAME);
        const sync = () => setSnapshot(buildSnapshot(service));
        sync();

        const unsubs = [
            service.subscribe(service.EVENTS.COLOR_3D_CHANGED, sync),
            service.subscribe(service.EVENTS.WINDOW_LEVEL_CHANGED, sync),
        ];
        return () => unsubs.forEach((unsub) => unsub());
    }, [buildSnapshot, getServiceManager]);

    return snapshot;
}
